// demoProtectedFields mixin: keeps identifying search/filter terms out of a
// component's model while Demo Mode protects the page.
//
// A term typed during ordinary work (an MBI, a surname, a practice name) still
// identifies a real patient, provider or customer after Demo Mode starts, and
// Demo Mode never rewrites form models. So a component lists the model paths
// that hold such terms:
//
//   import demoProtectedFields from '@/mixins/demoProtectedFields';
//   export default {
//     mixins: [demoProtectedFields],
//     demoProtectedFields: ['filters.mbi', 'filters.lastName'],
//     ...
//   }
//
// When Demo Mode starts, every listed path is reset to its empty value (''
// for strings, [] for arrays, null otherwise). While it stays on (activating,
// ready or failed), a non-empty value is reset again whenever it arrives:
//   - when the component is created while Demo Mode is on,
//   - whenever anything assigns one (storage restore, a route-query parse, a
//     late async response, a replaced parent object),
// unless the presenter typed that value into a `data-demo-search` box during
// this Demo Mode session (utils/demoSearchInput). So search keeps working in
// a demo: the presenter types, sees what they typed, and gets masked results,
// while no term from before the demo can come back. The watchers run with
// flush: 'sync', so the reset lands in the same tick as the assignment and no
// render ever sees the raw term.
//
// It never writes a fake value, never submits or saves anything, and does
// nothing once Demo Mode is off (it does not put the old terms back; they are
// gone). Each listed path must be bound to an input tagged class="sensitive"
// AND marked `data-demo-search`: the tag lets the engine turn off the
// browser's autocomplete (its dropdown of past entries is outside the page),
// and the marker keeps the box editable instead of masked and read-only
// (scripts/demo-coverage enforces both).
//
// `demoProtectedFields` may also be a function of the instance returning the
// paths, for components whose identifying fields depend on props (a generic
// table protecting the text filters of its sensitive columns). A path is a
// dotted string or an array of keys (for keys that contain dots). Only the
// static array form is checked by scripts/demo-coverage.
//
// Optional hook: a component may define a method
// `onDemoProtectedFieldsCleared(paths)`. It runs synchronously after the mixin
// emptied at least one path, so the component can keep its own persistence and
// derived state in step (rewrite a sessionStorage snapshot without the terms,
// tell a parent grid its filter changed). The hook decides what that costs;
// the mixin itself performs no I/O.

import { isDemoMode } from '@/utils/demoSpoof';
import { installDemoSearchInput, presenterTyped } from '@/utils/demoSearchInput';

function segments(path) {
  if (Array.isArray(path)) return path.map(String);
  return String(path).split('.').filter(Boolean);
}

function readPath(vm, path) {
  let cur = vm;
  for (const key of segments(path)) {
    if (cur == null) return undefined;
    cur = cur[key];
  }
  return cur;
}

function isEmptyValue(v) {
  if (v == null || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

function emptyLike(v) {
  if (typeof v === 'string') return '';
  if (Array.isArray(v)) return [];
  return null;
}

// Empties one path in place. Returns true when a value was actually removed.
function clearPath(vm, path) {
  const keys = segments(path);
  const last = keys.pop();
  let parent = vm;
  for (const key of keys) {
    if (parent == null) return false;
    parent = parent[key];
  }
  if (parent == null || typeof parent !== 'object' || last === undefined) return false;
  const current = parent[last];
  if (isEmptyValue(current)) return false;
  parent[last] = emptyLike(current);
  return true;
}

export default {
  created() {
    const option = this.$options.demoProtectedFields;
    if (typeof option !== 'function' && !(Array.isArray(option) && option.length)) return;
    installDemoSearchInput();
    const resolvePaths = () => {
      const paths = typeof option === 'function' ? option.call(this, this) : option;
      return Array.isArray(paths) ? paths : [];
    };

    // Emptying one path re-triggers the value watcher below synchronously;
    // the flag keeps that nested run from clearing (and reporting) twice.
    let clearing = false;
    const clear = (targets) => {
      if (clearing) return;
      clearing = true;
      let cleared;
      try {
        cleared = targets.filter((p) => clearPath(this, p));
      } finally {
        clearing = false;
      }
      if (cleared.length && typeof this.onDemoProtectedFieldsCleared === 'function') {
        this.onDemoProtectedFieldsCleared(cleared);
      }
    };

    this.$watch(
      () => isDemoMode(),
      (on) => {
        if (on) clear(resolvePaths());
      },
      { flush: 'sync' }
    );

    // One watcher over every listed value. It re-runs when a value is assigned,
    // when a parent object is replaced, when an array is mutated, and (for the
    // function form) when the list of paths itself changes.
    // While on, a value the presenter typed into the search box during this
    // Demo Mode session stays (see utils/demoSearchInput); anything else that
    // arrives (a restore, a late response, a replaced parent) is emptied.
    const unexpected = () =>
      resolvePaths().filter((p) => {
        const v = readPath(this, p);
        return !isEmptyValue(v) && !presenterTyped(v);
      });
    this.$watch(
      () => resolvePaths().map((p) => readPath(this, p)),
      () => {
        if (!isDemoMode()) return;
        const paths = unexpected();
        if (paths.length) clear(paths);
      },
      { flush: 'sync', deep: true }
    );

    if (isDemoMode()) {
      const paths = unexpected();
      if (paths.length) clear(paths);
    }
  },
};
